import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  ChannelPill,
  TemplateEditor,
} from '@/features/communications';
import type { CommTemplate, CreateTemplateInput, TemplateType, UpdateTemplateInput } from '@/features/communications';

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'INVOICE', label: 'Invoice' },
  { value: 'PROMO', label: 'Promo' },
  { value: 'REORDER_LINK', label: 'Reorder Link' },
  { value: 'FEEDBACK_REQUEST', label: 'Feedback' },
];

// The filter bar is a backdrop-blur glass card, which creates its own stacking
// context and traps the Type Select's z-50 inside the panel. Without an explicit
// z-index, the opaque templates table below — including its sticky header at
// z-10 — paints over the open dropdown, so the type options (Invoice / Promo /
// Reorder Link / Feedback) collide with the table's columns/rows. `relative z-20`
// lifts the panel above the table's sticky header so the dropdown overlays
// cleanly. Regression: GitHub issue #139 (mirrors #137).
export const FILTER_BAR_CLASS =
  'relative z-20 rounded-xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-md p-4 sm:p-5 mb-6';

type Row = CommTemplate;

function TemplatesContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const type = (searchParams.get('type') ?? '') as TemplateType | '';

  const { data, isLoading, isFetching } = useTemplates({ page, type });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();
  const deleteMutation = useDeleteTemplate();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CommTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CommTemplate | null>(null);

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function openEditor(template?: CommTemplate) {
    setEditingTemplate(template ?? null);
    setEditorOpen(true);
  }

  function closeEditor() {
    setEditorOpen(false);
    setEditingTemplate(null);
    triggerRef.current?.focus();
  }

  const handleSave = useCallback(
    (input: CreateTemplateInput | { id: string; input: UpdateTemplateInput }) => {
      if ('id' in input) {
        updateMutation.mutate(
          { id: input.id, input: input.input },
          {
            onSuccess: () => {
              toast({ variant: 'success', title: 'Template updated' });
              closeEditor();
            },
            onError: (err) => {
              toast({ variant: 'error', title: 'Update failed', description: (err as Error).message });
            },
          },
        );
      } else {
        createMutation.mutate(input, {
          onSuccess: () => {
            toast({ variant: 'success', title: 'Template created' });
            closeEditor();
          },
          onError: (err) => {
            toast({ variant: 'error', title: 'Creation failed', description: (err as Error).message });
          },
        });
      }
    },
    [createMutation, updateMutation, toast],
  );

  function handleDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Template deleted' });
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast({ variant: 'error', title: 'Delete failed', description: (err as Error).message });
        setDeleteTarget(null);
      },
    });
  }

  const editableColumns: ColumnDef<Row>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Template',
        render: (_v, row) => (
          <button
            type="button"
            onClick={() => openEditor(row)}
            className="group flex items-center gap-2 -my-1 rounded-lg px-2 py-1 transition-colors hover:bg-teal-50 dark:hover:bg-teal-900/20 text-left"
          >
            <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-teal-700 dark:group-hover:text-teal-300">
              {row.name}
            </p>
            {row.is_default ? (
              <span className="text-amber-500" title="Default template">
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </span>
            ) : null}
          </button>
        ),
      },
      {
        key: 'channel',
        header: 'Channel',
        render: (_v, row) => <ChannelPill channel={row.channel} />,
      },
      {
        key: 'type',
        header: 'Type',
        render: (_v, row) => (
          <span className="inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 uppercase tracking-wider">
            {row.type.replace(/_/g, ' ')}
          </span>
        ),
      },
      {
        key: 'created_at',
        header: 'Created',
        render: (_v, row) => (
          <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
            {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
          </span>
        ),
      },
      {
        key: 'id',
        header: '',
        width: '40px',
        render: (_v, row) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
            className="rounded-lg p-1.5 text-neutral-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
            aria-label={`Delete template ${row.name}`}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(20,184,166,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Message Templates
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Templates</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Create reusable templates for invoices, promotions, and feedback requests across all channels.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
              <span className="rounded-full bg-white/15 backdrop-blur-sm px-3 py-1 font-semibold tabular-nums font-mono">
                {(data?.meta.total ?? 0).toLocaleString('en-IN')} templates
              </span>
            </div>
          </div>
          <Button
            ref={triggerRef}
            variant="primary"
            onClick={() => openEditor()}
            className="!bg-white !text-teal-700 hover:!bg-white/90 !border-transparent"
          >
            + New Template
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className={FILTER_BAR_CLASS}>
        <div className="w-full lg:w-44">
          <Select
            label="Type"
            options={TYPE_OPTIONS}
            value={type}
            onChange={(v) => setParam('type', typeof v === 'string' ? v : '')}
          />
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No templates"
            description="Create your first message template to start automating communications."
            action={{ label: '+ Create Template', onClick: () => openEditor() }}
            icon={
              <svg className="h-20 w-20 text-teal-300 dark:text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Templates list">
          <DataTable<Row>
            columns={editableColumns}
            data={rows}
            loading={isFetching}
            totalCount={data?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => setParam('page', String(p))}
            stickyHeader
          />
        </section>
      )}

      {/* Template Editor Modal */}
      <TemplateEditor
        open={editorOpen}
        onClose={closeEditor}
        template={editingTemplate}
        onSave={handleSave}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Template"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export function TemplatesPage() {
  return (
    <PageContainer
      title="Templates"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Templates' },
      ]}
    >
      <ErrorBoundary resetKey="comm-templates">
        <TemplatesContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
